import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

export const UserTable = ({ users, selectedUsers, onUserSelect, onSelectAll, onUserClick, selectedUser, }) => {

  const parentUsers = users.filter(user => user.parent_id === null)
  const childUsersMap = users.reduce((acc, user) => {
    if (user.parent_id !== null) {
      if (!acc[user.parent_id]) acc[user.parent_id] = [];
      acc[user.parent_id].push(user);
    }
    return acc;
  }, {})
  const [expandedRows, setExpandedRows] = useState([])

  const toggleExpand = (userId) => {
    setExpandedRows(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId])
  }

  const formatLastActivity = (date) => {
    if (!date) return "No activity";    
    const now = new Date();
    const d = new Date(date);
    const diff = Math.floor((now - d)/1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 84600) return `${Math.floor(diff/3600)}h ago`;
    return `${Math.floor(diff/84600)}d ago`;
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return 'bg-accent-100 text-accent-700';
      // case '': return 'bg-primary-100 text-primary-700';
      case 'Client': return 'bg-secondary-100 text-secondary-700';
      case 'Staff': return 'bg-warning-100 text-warning-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    return status === 'Active' ? 'bg-success-100 text-success-700' : 'bg-error-100 text-error-700';
  };

  const isOnline = (lastActivity) => {
    if (!lastActivity) {
      return false; // If lastActivity is undefined or null, consider the user as offline
    }
    return Date.now() - new Date(lastActivity).getTime() < 900000; // 15 minutes
  };

  const handleUserClick = (user) => {
    // if (user.role === 'Client') {
    //   navigate(`/product-management?client_id=${user.id}`)
    // } else {
      onUserClick(user);
      toggleExpand(user.id);
    // }
  }

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      {/* Table Header */}
      <div className="px-6 py-4 border-b border-border bg-background">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-heading font-semibold text-text-primary">Team Members</h3>
          <div className="flex items-center space-x-2 text-sm text-text-secondary">
            <span>{users.length} users</span>
            <span>•</span>
            <span>{selectedUsers.length} selected</span>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-background sticky top-0 border-b border-border">
            <tr>
              <th className="pl-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedUsers.length === users.length && users.length > 0}
                  onChange={onSelectAll}
                  className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                />
              </th>
              <th className="pl-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                User
              </th>
              <th className="pl-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Role
              </th>
              <th className="pl-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Permissions
              </th>
              <th className="pl-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Last Activity
              </th>
              <th className="pl-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {parentUsers.map((user) => (
              <React.Fragment key={user.id}>
                <tr
                  className={`hover:bg-background nav-transition cursor-pointer ${selectedUser?.id === user.id ? 'bg-primary-50' : ''
                    }`}
                  onClick={() => handleUserClick(user)}
                >
                  <td className="pl-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        onUserSelect(user.id);
                      }}
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                    />
                  </td>
                  <td className="pl-6 py-4">
                    <div className='flex  items-center space-x-2'>
                      <button onClick={(e) => { e.stopPropagation(); toggleExpand(user.id); }}>
                        <Icon name={expandedRows.includes(user.id) ? 'ChevronDown' : 'ChevronRight'} size={16} />
                      </button>
                      <div>{user.company_name}</div>
                    </div>
                  </td>
                  <td className="pl-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="pl-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.permissions.slice(0, 2).map((permission, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded">
                          {permission}
                        </span>
                      ))}
                      {user.permissions.length > 2 && (
                        <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          +{user.permissions.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="pl-6 py-4 text-sm text-text-secondary">
                    {formatLastActivity(user.lastActivity)}
                  </td>
                  <td className="pl-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onUserClick(user);
                        }}
                        className="p-1 text-text-secondary hover:text-primary nav-transition"
                        title="Edit user"
                      >
                        <Icon name="Edit" size={16} color="currentColor" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('More actions for user:', user.id);
                        }}
                        className="p-1 text-text-secondary hover:text-primary nav-transition"
                        title="More actions"
                      >
                        <Icon name="MoreVertical" size={16} color="currentColor" />
                      </button>
                    </div>
                  </td>
                  {/* Other Columns as before */}
                </tr>

                {/* Accordion Child Rows */}
                {expandedRows.includes(user.id) && childUsersMap[user.id]?.map((child) => (
                  <tr key={child.id} className="bg-gray-50" onClick={() => onUserClick(child)}>
                    <td className="pl-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(child.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          onUserSelect(child.id);
                        }}
                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
                      />
                    </td>
                    <td className="py-4 pl-12">
                      {child.company_name}
                    </td>
                    <td className="pl-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(child.role)}`}>
                        {child.role}
                      </span>
                    </td>
                    <td className="pl-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {child.permissions.slice(0, 2).map((permission, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded">
                            {permission}
                          </span>
                        ))}
                        {child.permissions.length > 2 && (
                          <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                            +{child.permissions.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="pl-6 py-4 text-sm text-text-secondary">
                      {formatLastActivity(child.lastActivity)}
                    </td>
                    <td className="pl-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(child.status)}`}>
                        {child.status}
                      </span>
                    </td>
                    <td className="pl-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onUserClick(child)
                          }}
                          className="p-1 text-text-secondary hover:text-primary nav-transition"
                          title="Edit user"
                        >
                          <Icon name="Edit" size={16} color="currentColor" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('More actions for user:', child.id);
                          }}
                          className="p-1 text-text-secondary hover:text-primary nav-transition"
                          title="More actions"
                        >
                          <Icon name="MoreVertical" size={16} color="currentColor" />
                        </button>
                      </div>
                    </td>
                    {/* Other columns for child as needed */}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {
        users.length === 0 && (
          <div className="pl-6 py-12 text-center">
            <div className="w-16 h-16 mx-auto bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <Icon name="Users" size={32} color="var(--color-primary)" />
            </div>
            <h3 className="text-lg font-medium text-text-primary mb-2">No users found</h3>
            <p className="text-text-secondary">Get started by adding your first team member.</p>
          </div>
        )
      }
    </div >
  );
};

export default UserTable;